import { FC } from 'app/FunctionalComponent'
import React, { useEffect, useState } from 'react'
import { sosToast } from 'common/components/toast'
import { apiTypes, apiPrintNode } from 'ui/api'
import { Button } from 'ui/components/common/button'
import { IconButton, solidIcons } from 'ui/components/common/icon'
import { t } from 'ui/components/i18n/i18n'
import { Center } from 'ui/components/layout/center'
import { l } from 'ui/lib/lodashImports'
import * as classes from './PrintStationList.module.scss'
import { PrintStationConfigModal } from '../print-station-config-modal'
import { Spacer } from 'ui/components/layout/spacer'
import { PrinterGroupModal } from '../printer-group-modal'
import { getCookieByKey } from 'ui/lib/cookieHelper'
import { sosUser } from 'ui/state'
import { fireAndForget } from 'ui/lib/async'

export const tPrefix = 'page.companyManagement.printersAndScales.printStations'

export const PrintStationList: FC = (props: {
	printerList: apiTypes.PrinterResponse[]
	credentials: apiTypes.PrintNodeCredentialsResponse
	locationId: string
}) => {
	const { printerList, credentials, locationId } = props

	const [printStationList, setPrintStationList] = useState<
		apiTypes.PrinterGroupResponse[]
	>([])
	const [editRowPrintStation, setEditRowPrintStation] = useState<number>(null)
	const [isFetchingPrintStations, setIsFetchingPrintStations] = useState<
		boolean
	>(false)
	const [isDeletingPrinterGroup, setIsDeletingPrinterGroup] = useState<boolean>(
		false,
	)
	const [
		isPrintStationConfigModalOpen,
		setIsPrintStationConfigModalOpen,
	] = useState<boolean>(false)
	const [selectedPrintStationConfig, setSelectedPrintStationConfig] = useState<
		apiTypes.PrinterGroupResponse
	>(null)
	const [isPrinterGroupModalOpen, setIsPrinterGroupModalOpen] = useState<
		boolean
	>(false)

	useEffect(() => {
		fireAndForget(scanPrintNode, 'Fetching Print Stations')
	}, [props.credentials]) // eslint-disable-line react-hooks/exhaustive-deps

	const scanPrintNode = async (): Promise<void> => {
		setIsFetchingPrintStations(true)

		const printerGroupResponse = await apiPrintNode.getPrinterGroupList(
			() => {},
			credentials.id,
		)

		if (printerGroupResponse.data) {
			setPrintStationList(printerGroupResponse.data)
		} else if (printerGroupResponse.error) {
			sosToast.sendApiErrorResponseToast(printerGroupResponse)
		}

		setIsFetchingPrintStations(false)
	}

	const assignComputer = async (): Promise<void> => {
		setIsPrinterGroupModalOpen(true)
	}

	return (
		<Center>
			<div className={classes.printStationList}>
				<div className={classes.printStationListTitleButtons}>
					<div className={classes.printStationListTitle}>
						{t('printStationsList', tPrefix)}
					</div>
					<div>
						<Button
							color={'blue'}
							isDisabled={isFetchingPrintStations || isDeletingPrinterGroup}
							testId={'print-station-list-assign-computer'}
							onClick={assignComputer}
						>
							{t('assignComputer', tPrefix)}
						</Button>
						<Button
							color={'green'}
							isDisabled={isFetchingPrintStations || isDeletingPrinterGroup}
							testId={'print-station-list-create-print-station'}
							onClick={() => {
								setSelectedPrintStationConfig(null)
								setIsPrintStationConfigModalOpen(true)
							}}
						>
							{t('createPrintStation', tPrefix)}
						</Button>
					</div>
				</div>
				<table cellPadding={0} cellSpacing={0}>
					<thead>
						<tr>
							<th>{t('printStationsListLabels', tPrefix)}</th>
							<th>{t('config', tPrefix)}</th>
						</tr>
					</thead>
					<tbody>
						{isFetchingPrintStations ? (
							<tr>
								<td colSpan={2}>{t('loadingPrintStation', tPrefix)}</td>
							</tr>
						) : (
							<>
								{printStationList.length > 0 ? (
									<>
										{l.map(printStationList, (station, idx) => {
											return (
												<tr
													key={idx}
													data-testid={`printStationListTable-${idx}`}
												>
													<td>{station.nickname}</td>
													<td>
														<div>
															<IconButton
																icon={
																	idx === editRowPrintStation
																		? solidIcons.faSave
																		: solidIcons.faPencilAlt
																}
																color={'black'}
																onClick={() => {
																	setSelectedPrintStationConfig(station)
																	setIsPrintStationConfigModalOpen(true)
																}}
																testId={'print-station-list-edit-save'}
																disabled={isDeletingPrinterGroup}
															></IconButton>

															<IconButton
																icon={solidIcons.faTrashAlt}
																color={'black'}
																onClick={async () => {
																	if (editRowPrintStation !== null) {
																		setEditRowPrintStation(null)
																	} else {
																		setIsDeletingPrinterGroup(true)

																		const printerGroupResponse = await apiPrintNode.deletePrinterGroup(
																			() => {},
																			credentials.id,
																			station.id,
																		)

																		if (printerGroupResponse.data) {
																			const selectedPrintStationCookie = getCookieByKey(
																				'x-printer-group-id',
																			)

																			if (
																				station.id ===
																				selectedPrintStationCookie
																			) {
																				document.cookie = `x-printer-group-id=; expires=Fri, 31 Dec 2019 12:00:00 UTC; path=/`
																				await sosUser.fetchPrintStation('N/A')
																			}

																			const printerGroupResponse = await apiPrintNode.getPrinterGroupList(
																				() => {},
																				credentials.id,
																			)
																			if (printerGroupResponse.data) {
																				setPrintStationList(
																					printerGroupResponse.data,
																				)
																			} else if (printerGroupResponse.error) {
																				sosToast.sendApiErrorResponseToast(
																					printerGroupResponse,
																				)
																			}
																		} else if (printerGroupResponse.error) {
																			sosToast.sendApiErrorResponseToast(
																				printerGroupResponse,
																			)
																		}

																		setIsDeletingPrinterGroup(false)
																	}
																}}
																testId={'print-station-list-delete'}
																disabled={isDeletingPrinterGroup}
															></IconButton>
														</div>
													</td>
												</tr>
											)
										})}
									</>
								) : (
									<tr>
										<td colSpan={2}>{t('noPrintStationFound', tPrefix)}</td>
									</tr>
								)}
							</>
						)}
					</tbody>
				</table>

				<Spacer height={'30px'} />

				<PrintStationConfigModal
					isModalOpen={isPrintStationConfigModalOpen}
					onModalClose={() => {
						setIsPrintStationConfigModalOpen(false)
					}}
					credentials={credentials}
					printStationList={printStationList}
					setPrintStationList={setPrintStationList}
					selectedPrintStationConfig={selectedPrintStationConfig}
					locationId={locationId}
				/>

				<PrinterGroupModal
					isModalOpen={isPrinterGroupModalOpen}
					onModalClose={() => {
						setIsPrinterGroupModalOpen(false)
					}}
					printStationList={printStationList}
				/>
			</div>
		</Center>
	)
}
